import React, { useCallback, useState } from 'react';

import { ListActionTarget } from '../../../../models/ListActionTarget';
import { targetToIconCode } from '../../utils/utils-target-to-icon-code';
import { ButtonGroup, GroupButton } from '../ButtonGroup';
import './UnavailableSelector.scss';
import { useTranslation } from 'react-i18next';

/**
 * Props for <UnavailableSelector>.
 */
interface UnavailableSelectorProps {
    /**
     * The chosen target (cart or list).
     */
    readonly target: ListActionTarget;

    /**
     * Callback that is called when a product should be added to a list.
     */
    readonly onAddToList: () => Promise<void>;
}

type ResultState = 'success' | 'failure';

export function UnavailableSelector({ target, onAddToList }: UnavailableSelectorProps) {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [resultState, setResultState] = useState<ResultState>();

    const handleClick = useCallback(() => {
        if (!loading) {
            setLoading(true);
            onAddToList()
                .then(() => setResultState('success'))
                .catch(() => setResultState('failure'))
                .finally(() => {
                    setLoading(false);
                    setTimeout(() => {
                        setResultState(undefined);
                    }, 2000);
                });
        }
    }, [loading, onAddToList]);

    if (target === ListActionTarget.Cart) {
        return <div className="unavailable-selector" />;
    }

    const labelText = t('addToList');

    return (
        <div className="unavailable-selector">
            <ButtonGroup size="lg">
                <GroupButton
                    aria-label={labelText}
                    onClick={handleClick}
                    label={labelText}
                    icon={targetToIconCode(target)}
                    loading={loading}
                    state="clickable"
                    result={resultState}
                />
            </ButtonGroup>
        </div>
    );
}
